import React from "react";
import {
  AppWindow,
  BadgeCheck,
  BarChart3,
  Boxes,
  Braces,
  Bug,
  CalendarRange,
  CheckSquare2,
  CloudCog,
  Code2,
  FileCheck2,
  FileSearch,
  Files,
  GitPullRequest,
  HeartPulse,
  Landmark,
  Layers3,
  PackageSearch,
  Scale,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import {
  ASSURANCE_VIEWS,
  ASSURANCE_VIEW_GROUPS,
  viewsInGroup,
} from "../../assurance/views";
import { cn } from "../../lib/utils";

const icons: Record<string, React.ElementType> = {
  capability: CheckSquare2,
  portfolio: Layers3,
  application: AppWindow,
  coverage: FileSearch,
  architecture: Braces,
  findings: Bug,
  evidence: Files,
  remediation: Wrench,
  "supply-chain": PackageSearch,
  licenses: Scale,
  cloud: CloudCog,
  quality: Code2,
  benchmark: BarChart3,
  health: HeartPulse,
  compliance: Landmark,
  campaign: CalendarRange,
  "pr-guard": GitPullRequest,
  reports: FileCheck2,
  deployment: ShieldCheck,
  replacement: BadgeCheck,
};

export function AssuranceViewIcon({
  viewId,
  className = "h-4 w-4",
}: {
  viewId: string;
  className?: string;
}) {
  const Icon = icons[viewId] || Boxes;
  return <Icon className={className} aria-hidden="true" />;
}

interface NavigationProps {
  activeView: string;
  onSelect: (view: string) => void;
  /** Per-view annotation counts (e.g. open findings) rendered as small badges. */
  counts?: Record<string, number | string>;
}

/**
 * Desktop secondary navigation (xl+): a flat rail attached to the global
 * sidebar — full height, shared border, no floating card — so the workspace
 * reads as one continuous application chrome.
 */
export function AssuranceRail({ activeView, onSelect, counts = {} }: NavigationProps) {
  return (
    <aside
      className="hidden w-[240px] flex-none border-r border-border/70 bg-surface1/60 xl:block"
      aria-label="Assurance sections"
    >
      <nav
        className="sticky top-0 max-h-dvh overflow-y-auto px-3 pb-8 pt-5"
        aria-label="Assurance views"
      >
        <div className="px-2.5 pb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-muted">
          Assurance
        </div>
        {ASSURANCE_VIEW_GROUPS.map((group) => (
          <div key={group} className="mt-4 first-of-type:mt-1">
            <div className="px-2.5 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted/90">
              {group}
            </div>
            <div className="space-y-px">
              {viewsInGroup(group).map((view) => {
                const active = activeView === view.id;
                const count = counts[view.id];
                return (
                  <button
                    key={view.id}
                    type="button"
                    onClick={() => onSelect(view.id)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "group flex w-full items-center gap-2.5 rounded-lg py-2 pl-2.5 pr-2 text-left text-[13px] font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                      active
                        ? "bg-gradient-to-r from-brand/15 to-brand/5 text-brand ring-1 ring-inset ring-brand/15"
                        : "text-text2 hover:bg-surface2 hover:text-text"
                    )}
                  >
                    <span
                      className={cn(
                        "h-[18px] w-[3px] flex-none rounded-full transition-colors",
                        active ? "bg-brand" : "bg-transparent group-hover:bg-border"
                      )}
                      aria-hidden="true"
                    />
                    <AssuranceViewIcon
                      viewId={view.id}
                      className={cn(
                        "h-4 w-4 flex-none",
                        !active && "text-muted group-hover:text-text2"
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{view.shortLabel}</span>
                    {count !== undefined && count !== 0 && (
                      <span
                        className={cn(
                          "flex-none rounded-full px-1.5 text-[10.5px] font-bold tabular-nums leading-[18px]",
                          active ? "bg-brand/15 text-brand" : "bg-surface2 text-muted"
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/**
 * Compact switcher for viewports below xl: a grouped select plus a
 * horizontally scrollable chip strip, rendered inside the content column.
 */
export function AssuranceViewSwitcher({ activeView, onSelect }: NavigationProps) {
  return (
    <div className="mb-4 space-y-2 xl:hidden">
      <label htmlFor="assurance-view-select" className="sr-only">
        Assurance view
      </label>
      <select
        id="assurance-view-select"
        className="input py-2 text-sm"
        value={activeView}
        onChange={(event) => onSelect(event.target.value)}
      >
        {ASSURANCE_VIEW_GROUPS.map((group) => (
          <optgroup key={group} label={group}>
            {viewsInGroup(group).map((view) => (
              <option key={view.id} value={view.id}>
                {view.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div
        className="flex gap-1 overflow-x-auto pb-1"
        role="tablist"
        aria-label="Assurance views"
      >
        {ASSURANCE_VIEWS.map((view) => (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={activeView === view.id}
            onClick={() => onSelect(view.id)}
            className={cn(
              "flex flex-none items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
              activeView === view.id
                ? "bg-brand/10 text-brand ring-1 ring-brand/25"
                : "bg-surface1 text-text2 ring-1 ring-border/60 hover:text-text"
            )}
          >
            <AssuranceViewIcon viewId={view.id} className="h-3.5 w-3.5" />
            {view.shortLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
